import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Undo2, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function BusinessInvoices({ business, onChange }) {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState(thisMonth());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/platform/businesses/${business.id}/invoices`, { headers: getAuthHeader() });
    setRows(r.data);
  }, [business.id, getAuthHeader]);

  useEffect(() => { load().catch(() => toast.error('Could not load invoices')); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const create = () => run(() => axios.post(`${API}/platform/businesses/${business.id}/invoices`, { period }, { headers: getAuthHeader() }), `Invoice for ${period} added`);
  const setStatus = (inv, status) => run(() => axios.put(`${API}/platform/invoices/${inv.id}`, { status }, { headers: getAuthHeader() }), status === 'paid' ? 'Marked paid' : 'Marked due');
  const remove = (inv) => run(() => axios.delete(`${API}/platform/invoices/${inv.id}`, { headers: getAuthHeader() }), 'Invoice removed');

  const due = rows.filter(r => r.status === 'due').reduce((a, r) => a + (r.total ?? r.amount), 0);

  return (
    <div className="space-y-4" data-testid="business-invoices">
      <p className="text-sm text-gray-600">
        Bank transfer billing: add the month's invoice, send it to <b>{business.contact_email || business.owner_email}</b>, then mark it paid when the money lands.
        {due > 0 && <span className="text-amber-700 font-medium"> Outstanding: {money(due)}.</span>}
      </p>
      <div className="flex items-center gap-2">
        <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" data-testid="invoice-period" />
        <Button size="sm" onClick={create} disabled={busy || !period} data-testid="invoice-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add {money(business.monthly_fee)} + GST invoice</Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">No invoices yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 text-left">
            <tr><th className="py-1">Period</th><th>Note</th><th className="text-right">Ex GST</th><th className="text-right">GST</th><th className="text-right">Total</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(inv => (
              <tr key={inv.id} className="border-t" data-testid={`invoice-row-${inv.period}`}>
                <td className="py-2 font-medium">{inv.period}</td>
                <td className="text-gray-500">{inv.note}</td>
                <td className="text-right">{money(inv.amount)}</td>
                <td className="text-right text-gray-500">{money(inv.gst ?? inv.amount * 0.15)}</td>
                <td className="text-right font-medium" data-testid="invoice-total">{money(inv.total ?? inv.amount * 1.15)}</td>
                <td>
                  <Badge className={inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'} data-testid="invoice-status">
                    {inv.status}{inv.paid_at ? ` · ${new Date(inv.paid_at).toLocaleDateString('en-NZ')}` : ''}
                  </Badge>
                </td>
                <td className="text-right whitespace-nowrap">
                  {inv.status === 'due'
                    ? <Button size="sm" variant="ghost" onClick={() => setStatus(inv, 'paid')} disabled={busy} data-testid="invoice-mark-paid"><Check className="w-3.5 h-3.5 mr-1" /> Paid</Button>
                    : <Button size="sm" variant="ghost" onClick={() => setStatus(inv, 'due')} disabled={busy} data-testid="invoice-mark-due"><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo</Button>}
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(inv)} disabled={busy} data-testid="invoice-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
